import ResourceCrudPage, {
  Field,
  TextInput,
  NumberInput,
  TextArea,
  StringListEditor,
  Tabs,
  Checkbox,
  Select,
  ColorInput,
  JsonEditor,
  isValidHex,
  isValidUrl,
} from "./ResourceCrudPage";

const IMAGE_KEYS = [
  "program_stem_coding",
  "program_tennis",
  "program_therapy",
  "program_camps",
  "hero_main",
  "hero_abstract",
];

const ICON_TYPES = [
  { value: "", label: "— Default —" },
  { value: "blocks", label: "Blocks (LEGO / building)" },
  { value: "gears", label: "Gears (engineering)" },
  { value: "robot", label: "Robot" },
  { value: "code", label: "Code blocks" },
  { value: "sun", label: "Sun (camps)" },
  { value: "heart", label: "Heart (support)" },
  { value: "spark", label: "Spark (activities)" },
  { value: "tennis", label: "Trophy (sports)" },
  { value: "puzzle", label: "Puzzle (developmental)" },
];

const PROGRAM_TYPES = [
  { value: "", label: "— Not set —" },
  { value: "term", label: "Term (school-year)" },
  { value: "camp", label: "Camp (seasonal)" },
  { value: "1on1", label: "1-on-1 sessions" },
  { value: "hub", label: "Hub (umbrella)" },
  { value: "waitlist", label: "Waitlist / Coming soon" },
];

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const TABS = [
  { key: "basic", label: "Basic Info" },
  { key: "content", label: "Page Content" },
  { key: "visual", label: "Visual / Theme" },
  { key: "seo", label: "SEO" },
  { key: "advanced", label: "Advanced JSON" },
];

export default function AdminProgramsPage() {
  return (
    <ResourceCrudPage
      resource="programs"
      title="Programs"
      subtitle="All public programs. Active programs appear on the site; hidden programs are kept in the database but not shown to visitors."
      testIdPrefix="programs"
      blank={() => ({
        slug: "",
        name: "",
        hero_title: "",
        tagline: "",
        category: "",
        ages: "",
        age_range: "",
        age_min: null,
        age_max: null,
        level: "",
        duration: "",
        class_length: "",
        format: "",
        description: "",
        short_description: "",
        full_description: "",
        hero_subtitle: "",
        highlights: [],
        why_choose: [],
        learning_outcomes: [],
        pathway: [],
        faqs: [],
        interests: [],
        goals: [],
        seasonal: false,
        beginner_friendly: true,
        show_ye_credibility: false,
        bookwhen_url: "",
        trial_price: "",
        program_notes: "",
        location_keywords: [],
        image_key: "program_stem_coding",
        image_url: "",
        logo_url: "",
        icon_type: "",
        brand_label: "",
        accent: "bg-sky-50 text-[#0369A1]",
        accent_color: "",
        secondary_color: "",
        cta_label: "Request information",
        display_order: 100,
        sort_order: 100,
        active: true,
        visible: true,
        is_featured: false,
        program_type: "",
        session_format: "",
        price: "",
        funding_eligibility: "",
        seo_title: "",
        seo_description: "",
      })}
      validate={(f) => {
        if (!f.name?.trim()) return { ok: false, message: "Title is required." };
        if (!f.slug?.trim()) return { ok: false, message: "Slug is required." };
        if (!isValidHex(f.accent_color))
          return { ok: false, message: "Accent colour must be a valid hex (e.g. #4CAF50)." };
        if (!isValidHex(f.secondary_color))
          return { ok: false, message: "Secondary colour must be a valid hex (e.g. #EAF7EA)." };
        if (!isValidUrl(f.logo_url))
          return { ok: false, message: "Logo URL must start with http:// or https://." };
        if (!isValidUrl(f.image_url))
          return { ok: false, message: "Image URL must start with http:// or https://." };
        if (!isValidUrl(f.bookwhen_url))
          return { ok: false, message: "Bookwhen URL must start with http:// or https://." };
        return { ok: true };
      }}
      renderRow={(item) => (
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            {item.accent_color && (
              <span
                className="h-3 w-3 rounded-full border border-white shadow-sm"
                style={{ backgroundColor: item.accent_color }}
                aria-hidden="true"
              />
            )}
            <span className="pill bg-sky-50 text-[#0369A1]">
              {item.category || "Program"}
            </span>
            <p className="font-display font-bold text-slate-900">{item.name}</p>
            {item.brand_label && (
              <span className="pill bg-amber-50 text-[#9A3412] text-[10px]">
                {item.brand_label}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1 truncate">
            /programs/{item.slug} · Ages {item.age_range || item.ages || "—"} ·{" "}
            {item.class_length || item.duration || "—"}
          </p>
        </div>
      )}
      renderForm={({ form, setField, errors, setError }) => (
        <Tabs items={TABS} testId="programs-tabs">
          {(activeTab) => (
            <>
              {/* ===== Basic Info ===== */}
              {activeTab === "basic" && (
                <div className="grid gap-5" data-testid="programs-tab-basic">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Title *">
                      <TextInput
                        value={form.name}
                        onChange={(v) => {
                          setField("name")(v);
                          if (!form.slug || form.slug === slugify(form.name)) {
                            setField("slug")(slugify(v));
                          }
                        }}
                        data-testid="programs-form-name"
                      />
                    </Field>
                    <Field label="Slug (URL) *" hint="/programs/{slug}">
                      <TextInput
                        value={form.slug}
                        onChange={setField("slug")}
                        data-testid="programs-form-slug"
                      />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <Field label="Category" hint="e.g. STEM & Engineering">
                      <TextInput value={form.category} onChange={setField("category")} />
                    </Field>
                    <Field label="Age range" hint="Display label, e.g. 6–10 years">
                      <TextInput value={form.age_range || form.ages} onChange={setField("age_range")} />
                    </Field>
                    <Field label="Class length / duration" hint="e.g. 8–12 week terms">
                      <TextInput value={form.class_length || form.duration} onChange={setField("class_length")} />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <Field label="Program type">
                      <Select
                        value={form.program_type}
                        onChange={setField("program_type")}
                        options={PROGRAM_TYPES}
                      />
                    </Field>
                    <Field label="Session format" hint="e.g. Weekly small group">
                      <TextInput value={form.session_format} onChange={setField("session_format")} />
                    </Field>
                    <Field label="Display order" hint="Lower = first">
                      <NumberInput
                        value={form.display_order}
                        onChange={setField("display_order")}
                        data-testid="programs-form-display-order"
                      />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <Field label="Price" hint="Display only — no checkout">
                      <TextInput value={form.price} onChange={setField("price")} />
                    </Field>
                    <Field label="Trial price" hint="Optional — shown next to trial CTA">
                      <TextInput value={form.trial_price} onChange={setField("trial_price")} />
                    </Field>
                    <Field label="Funding eligibility" hint="e.g. Autism Funding accepted">
                      <TextInput value={form.funding_eligibility} onChange={setField("funding_eligibility")} />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Bookwhen URL" hint="Optional. If set, a 'View schedule' button appears." error={errors?.bookwhen_url}>
                      <TextInput
                        value={form.bookwhen_url}
                        onChange={(v) => {
                          setField("bookwhen_url")(v);
                          setError("bookwhen_url", isValidUrl(v) ? "" : "Must start with http:// or https://");
                        }}
                        data-testid="programs-form-bookwhen-url"
                      />
                    </Field>
                    <Field label="Program notes" hint="Internal note shown only in admin lists">
                      <TextInput value={form.program_notes} onChange={setField("program_notes")} />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-slate-200">
                    <Checkbox
                      value={form.active}
                      onChange={setField("active")}
                      label="Active (visible on public site)"
                      data-testid="programs-form-active"
                    />
                    <Checkbox
                      value={form.beginner_friendly}
                      onChange={setField("beginner_friendly")}
                      label="Beginner friendly"
                      hint="Shows 'Beginner friendly' badge on the landing page"
                    />
                    <Checkbox
                      value={form.is_featured}
                      onChange={setField("is_featured")}
                      label="Featured on /programs Hub (Row 1)"
                      hint="Featured programs render as large cards in the Hub's top row. Up to 3."
                      data-testid="programs-form-is-featured"
                    />
                  </div>
                </div>
              )}

              {/* ===== Page Content ===== */}
              {activeTab === "content" && (
                <div className="grid gap-5" data-testid="programs-tab-content">
                  <Field label="Hero title (long SEO H1)" hint="Shown on /programs/{slug}. Falls back to Title if empty.">
                    <TextInput
                      value={form.hero_title}
                      onChange={setField("hero_title")}
                      data-testid="programs-form-hero-title"
                      placeholder="e.g. STEM Classes for Kids in Richmond BC"
                    />
                  </Field>
                  <Field label="Hero subtitle" hint="One paragraph under the H1.">
                    <TextArea
                      rows={2}
                      value={form.hero_subtitle}
                      onChange={setField("hero_subtitle")}
                    />
                  </Field>
                  <Field label="Short description" hint="One-line summary used in cards and SEO snippets.">
                    <TextInput
                      value={form.short_description}
                      onChange={setField("short_description")}
                    />
                  </Field>
                  <Field label="Full description" hint="Used in the Program Overview section. Use blank lines to separate paragraphs.">
                    <TextArea
                      rows={6}
                      value={form.full_description}
                      onChange={setField("full_description")}
                      data-testid="programs-form-full-description"
                    />
                  </Field>
                  <Field label="Highlights / bullets" hint="Shown in the hero and Inside-every-session list.">
                    <StringListEditor
                      value={form.highlights}
                      onChange={setField("highlights")}
                      placeholder="Add a highlight"
                    />
                  </Field>
                  <Field label="Learning outcomes" hint="Shown in the sidebar 'Students develop:' list.">
                    <StringListEditor
                      value={form.learning_outcomes}
                      onChange={setField("learning_outcomes")}
                      placeholder="Add a learning outcome"
                    />
                  </Field>
                  <Field label="Why families choose Nexora" hint="Shown in the dark sidebar card.">
                    <StringListEditor
                      value={form.why_choose}
                      onChange={setField("why_choose")}
                      placeholder="Add a reason"
                    />
                  </Field>
                  <Field label="CTA label" hint="Button text on cards (e.g. Book a trial class).">
                    <TextInput value={form.cta_label} onChange={setField("cta_label")} />
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-slate-200">
                    <Checkbox
                      value={form.show_ye_credibility}
                      onChange={setField("show_ye_credibility")}
                      label="Show Young Engineers credibility block"
                      hint="Toggles the international-curriculum trust block on the landing page."
                    />
                    <Checkbox
                      value={form.seasonal}
                      onChange={setField("seasonal")}
                      label="Mark as seasonal"
                      hint="Adds a 'Seasonal' badge to the program card."
                    />
                  </div>
                </div>
              )}

              {/* ===== Visual / Theme ===== */}
              {activeTab === "visual" && (
                <div className="grid gap-5" data-testid="programs-tab-visual">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field
                      label="Accent colour"
                      hint="Primary hex used for hero CTA, accent strip, and pathway tier."
                      error={errors?.accent_color}
                    >
                      <ColorInput
                        value={form.accent_color}
                        onChange={(v) => {
                          setField("accent_color")(v);
                          setError("accent_color", isValidHex(v) ? "" : "Must be #RGB or #RRGGBB");
                        }}
                        data-testid="programs-form-accent-color"
                      />
                    </Field>
                    <Field
                      label="Secondary colour"
                      hint="Soft background tint behind hero + logo."
                      error={errors?.secondary_color}
                    >
                      <ColorInput
                        value={form.secondary_color}
                        onChange={(v) => {
                          setField("secondary_color")(v);
                          setError("secondary_color", isValidHex(v) ? "" : "Must be #RGB or #RRGGBB");
                        }}
                        data-testid="programs-form-secondary-color"
                      />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field
                      label="Logo URL"
                      hint="Direct image URL. Leave blank to fall back to icon below."
                      error={errors?.logo_url}
                    >
                      <TextInput
                        value={form.logo_url}
                        onChange={(v) => {
                          setField("logo_url")(v);
                          setError("logo_url", isValidUrl(v) ? "" : "Must start with http:// or https://");
                        }}
                        placeholder="https://nexora.app/logos/lego-engineering.svg"
                        data-testid="programs-form-logo-url"
                      />
                    </Field>
                    <Field label="Icon type" hint="Used when logo URL is empty.">
                      <Select
                        value={form.icon_type}
                        onChange={setField("icon_type")}
                        options={ICON_TYPES}
                        data-testid="programs-form-icon-type"
                      />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field
                      label="Hero image URL"
                      hint="Overrides the gradient placeholder when set."
                      error={errors?.image_url}
                    >
                      <TextInput
                        value={form.image_url}
                        onChange={(v) => {
                          setField("image_url")(v);
                          setError("image_url", isValidUrl(v) ? "" : "Must start with http:// or https://");
                        }}
                        data-testid="programs-form-image-url"
                      />
                    </Field>
                    <Field label="Image preset" hint="Legacy fallback when no image URL is provided.">
                      <Select
                        value={form.image_key}
                        onChange={setField("image_key")}
                        options={IMAGE_KEYS}
                      />
                    </Field>
                  </div>
                  <Field label="Brand label" hint="Small tagline shown above the H1 and on cards. e.g. 'Brick Challenge · Galileo Technic'.">
                    <TextInput
                      value={form.brand_label}
                      onChange={setField("brand_label")}
                      data-testid="programs-form-brand-label"
                    />
                  </Field>
                  {form.accent_color && form.secondary_color && (
                    <div className="rounded-2xl border border-slate-200 p-4 flex items-center gap-3" data-testid="programs-form-color-preview">
                      <span className="text-xs uppercase tracking-wider font-bold text-slate-500">Preview</span>
                      <div
                        className="flex-1 rounded-xl p-3 text-sm font-semibold flex items-center justify-between"
                        style={{ backgroundColor: form.secondary_color, color: form.accent_color }}
                      >
                        <span>{form.name || "Program name"}</span>
                        <span
                          className="rounded-full px-3 py-1 text-xs text-white"
                          style={{ backgroundColor: form.accent_color }}
                        >
                          CTA
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ===== SEO ===== */}
              {activeTab === "seo" && (
                <div className="grid gap-5" data-testid="programs-tab-seo">
                  <Field label="SEO title" hint="Used in <title> + Google search snippet. Leave blank to use Hero title.">
                    <TextInput
                      value={form.seo_title}
                      onChange={setField("seo_title")}
                      data-testid="programs-form-seo-title"
                    />
                  </Field>
                  <Field label="Meta description" hint="155–160 chars recommended for Google snippet.">
                    <TextArea
                      rows={3}
                      value={form.seo_description}
                      onChange={setField("seo_description")}
                      data-testid="programs-form-seo-description"
                    />
                  </Field>
                  <Field label="Location keywords" hint="e.g. Richmond BC, Delta. Used as content cues.">
                    <StringListEditor
                      value={form.location_keywords}
                      onChange={setField("location_keywords")}
                      placeholder="Add a location keyword"
                    />
                  </Field>
                </div>
              )}

              {/* ===== Advanced JSON ===== */}
              {activeTab === "advanced" && (
                <div className="grid gap-6" data-testid="programs-tab-advanced">
                  <Field
                    label="Pathway tracks (JSON)"
                    hint='Array of {"title","subtitle","age_range","description","concepts":[],"projects":[],"accent_color","secondary_color"}.'
                    error={errors?.pathway}
                  >
                    <JsonEditor
                      value={form.pathway}
                      onChange={setField("pathway")}
                      onValidityChange={(ok, msg) => setError("pathway", ok ? "" : msg)}
                      rows={10}
                      testId="programs-form-pathway"
                    />
                  </Field>
                  <Field
                    label="FAQs (JSON)"
                    hint='Array of {"question","answer"}. Appears on landing page + powers FAQPage schema.'
                    error={errors?.faqs}
                  >
                    <JsonEditor
                      value={form.faqs}
                      onChange={setField("faqs")}
                      onValidityChange={(ok, msg) => setError("faqs", ok ? "" : msg)}
                      rows={10}
                      testId="programs-form-faqs"
                    />
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Interests" hint="Used by the recommendation widget.">
                      <StringListEditor
                        value={form.interests}
                        onChange={setField("interests")}
                        placeholder="e.g. Building"
                      />
                    </Field>
                    <Field label="Goals" hint="Used by the recommendation widget.">
                      <StringListEditor
                        value={form.goals}
                        onChange={setField("goals")}
                        placeholder="e.g. Confidence"
                      />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Age min (number)">
                      <NumberInput value={form.age_min ?? 0} onChange={setField("age_min")} />
                    </Field>
                    <Field label="Age max (number)">
                      <NumberInput value={form.age_max ?? 0} onChange={setField("age_max")} />
                    </Field>
                  </div>
                </div>
              )}
            </>
          )}
        </Tabs>
      )}
    />
  );
}
